import clsx from 'clsx'
import isArray from 'lodash/isArray'
import map from 'lodash/map'
import React, { FC, Fragment, useCallback } from 'react'

import SvgCheck from '@Assets/SVG/Check'
import { useTranslation } from '@Globals/Translations'

import styles from './BubbleNavigation.module.scss'

type NavigationStepKey = string
export interface NavigationStep<T> {
  /** Fallback when `title` is an i18n key missing from the loaded locale. */
  fallback?: string
  key: T
  title: string | string[]
}
export type BubbleNavigationProps<T> = {
  activeStep: NavigationStep<T>
  className?: string
  stepValidations: Record<NavigationStepKey, boolean>
  steps: NavigationStep<T>[]
  /** 'plain': compact inline labels, no connecting lines. */
  variant?: 'checkout' | 'plain'
} & (
  // interactive: false renders static markers; otherwise a click handler is required.
  | { interactive: false, onClick?: (step: NavigationStep<T>) => void }
  | { interactive?: true, onClick: (step: NavigationStep<T>) => void }
)

export const BubbleNavigation: FC<BubbleNavigationProps<string>> = ({
  activeStep,
  className,
  interactive = true,
  onClick,
  stepValidations,
  steps,
  variant = 'checkout',
}) => {
  const isPlain = variant === 'plain'
  const { t } = useTranslation()
  const isPrevActive = useCallback((index: number) => steps.findIndex(step => activeStep.key === (step.key)) < index, [activeStep, steps])
  const isActive = useCallback(step => activeStep.key === step.key, [activeStep])
  const isStepValid = useCallback(
    (step, index) => !isActive(step) && !isPrevActive(index) && stepValidations[step.key],
    [isActive, isPrevActive, stepValidations],
  )
  const getBubbleContent = useCallback((step, index) => {
    if (isPlain && isStepValid(step, index)) {
      return <SvgCheck />
    }
    return (
      <span className={styles.number}>
        {index + 1}
      </span>
    )
  }, [isPlain, isStepValid])

  return (
    <div className={clsx(styles.bubbleNavigation, isPlain && styles.plain, className)}>
      {map(steps, (step, index) => {
        const stepClassName = clsx(
          styles.step,
          isActive(step) && styles.active,
          isStepValid(step, index) && styles.valid,
        )
        const stepContent = (
          <>
            <span className={styles.bubble}>{getBubbleContent(step, index)}</span>
            <div className={styles.name}>
              {isArray(step.title) ? step.title.map(a => t(a)).join(' & ') : t(step.title, step.fallback ?? step.title)}
            </div>
          </>
        )

        return (
          <Fragment key={`step-${step.key}`}>
            {interactive
              ? (
                <button aria-current={isActive(step) ? 'step' : undefined} className={stepClassName} onClick={() => onClick?.(step)} type="button">
                  {stepContent}
                </button>
              )
              : (
                <div aria-current={isActive(step) ? 'step' : undefined} className={stepClassName}>{stepContent}</div>
              )}
            {!isPlain && index < steps.length - 1 && (
              <div className={clsx(styles.line, !isActive(step) && !isPrevActive(index) && styles.valid)} />
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
